import { clsx } from 'clsx'
import { AlertCircle, BarChart3, FolderTree, LayoutDashboard, LogOut, Menu, Moon, Radio, SlidersHorizontal, Sun, UserPlus, Users, X } from 'lucide-react'
import { useState } from 'react'
import { Link, Outlet, useLocation } from 'react-router-dom'

import { useAuth } from '../hooks/useAuth'
import { usePublicationQueue } from '../hooks/useCategorisations'
import { useTheme } from '../hooks/useTheme'
import { useWebhookEventStats } from '../hooks/useWebhookEvents'

/**
 * Top navigation bar with layout wrapper.
 * Uses <Outlet /> to render child routes (shared layout pattern).
 * Dark theme with glassmorphism navbar and neon cyan accents.
 * Includes mobile hamburger menu with slide-out drawer.
 */
export function Navbar() {
  const { user, signOut } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const location = useLocation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const { data: wachtrijData } = usePublicationQueue()
  const wachtend = wachtrijData?.total ?? 0
  const { data: webhookStats } = useWebhookEventStats()
  const webhookFailures = (webhookStats?.failed ?? 0) + (webhookStats?.dead ?? 0)

  // De wachtrij is de startpagina (G-06), dus het meest linkse kopje.
  const navLinks = [
    { to: '/wachtrij', label: 'Wachtrij', icon: AlertCircle, badge: wachtend },
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, badge: 0 },
    { to: '/analytics', label: 'Analytics', icon: BarChart3, badge: 0 },
    { to: '/webhooks', label: 'Webhooks', icon: Radio, badge: webhookFailures },
    { to: '/vault-entities', label: 'Vault', icon: FolderTree, badge: 0 },
    { to: '/clients', label: 'Clients', icon: Users, badge: 0 },
    { to: '/team', label: 'Team', icon: UserPlus, badge: 0 },
    { to: '/settings', label: 'Settings', icon: SlidersHorizontal, badge: 0 },
  ]

  // Ook onderliggende pagina's tellen: een open dossier (/wachtrij/:id) hoort bij Wachtrij.
  const isActief = (to: string) => location.pathname === to || location.pathname.startsWith(`${to}/`)

  return (
    <div className="min-h-screen bg-background">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-accent focus:text-background focus:rounded-lg focus:text-sm focus:font-medium"
      >
        Skip to main content
      </a>
      <nav aria-label="Main navigation" className="bg-background/95 backdrop-blur-md border-b border-border sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <Link to="/wachtrij" className="flex-shrink-0 flex items-center">
                <img
                  src={theme === 'light' ? '/andai-logo-white.png' : '/andai-logo.webp'}
                  alt="And AI"
                  className="h-8 w-auto"
                />
              </Link>
              <div className="hidden sm:ml-8 sm:flex sm:space-x-1">
                {navLinks.map(({ to, label, icon: Icon, badge }) => (
                  <Link
                    key={to}
                    to={to}
                    className={clsx(
                      'inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors',
                      isActief(to)
                        ? 'text-accent bg-accent/10'
                        : 'text-foreground-muted hover:text-foreground hover:bg-background-surface'
                    )}
                  >
                    <Icon className="w-4 h-4 mr-1.5" />
                    {label}
                    {badge > 0 && (
                      <span className="ml-1.5 px-1.5 py-0.5 text-xs font-medium rounded-full bg-amber-500/20 text-amber-400">
                        {badge}
                      </span>
                    )}
                  </Link>
                ))}
              </div>
            </div>
            <div className="flex items-center">
              <span className="hidden sm:block text-sm text-foreground-muted mr-4">{user?.email}</span>
              <button
                onClick={toggleTheme}
                className="hidden sm:block text-foreground-muted hover:text-accent p-2 rounded-lg hover:bg-accent/10 transition-colors"
                title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
                aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
              >
                {theme === 'dark' ? (
                  <Sun className="w-5 h-5" aria-hidden="true" />
                ) : (
                  <Moon className="w-5 h-5" aria-hidden="true" />
                )}
              </button>
              <button
                onClick={() => signOut()}
                className="hidden sm:block text-foreground-muted hover:text-accent p-2 rounded-lg hover:bg-accent/10 transition-colors"
                title="Sign out"
                aria-label="Sign out"
              >
                <LogOut className="w-5 h-5" aria-hidden="true" />
              </button>
              {/* Mobile menu button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="sm:hidden text-foreground-muted hover:text-foreground p-2 rounded-lg hover:bg-background-surface transition-colors"
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={mobileMenuOpen}
              >
                {mobileMenuOpen ? (
                  <X className="w-5 h-5" aria-hidden="true" />
                ) : (
                  <Menu className="w-5 h-5" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu drawer */}
        {mobileMenuOpen && (
          <div className="sm:hidden border-t border-border bg-background-secondary">
            <div className="px-4 py-3 space-y-1">
              {navLinks.map(({ to, label, icon: Icon, badge }) => (
                <Link
                  key={to}
                  to={to}
                  onClick={() => setMobileMenuOpen(false)}
                  className={clsx(
                    'flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors',
                    isActief(to)
                      ? 'text-accent bg-accent/10'
                      : 'text-foreground-muted hover:text-foreground hover:bg-background-surface'
                  )}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {label}
                  {badge > 0 && (
                    <span className="ml-auto px-1.5 py-0.5 text-xs font-medium rounded-full bg-amber-500/20 text-amber-400">
                      {badge}
                    </span>
                  )}
                </Link>
              ))}
            </div>
            <div className="px-4 py-3 border-t border-border flex items-center justify-between">
              <span className="text-sm text-foreground-muted truncate">{user?.email}</span>
              <div className="flex items-center gap-1">
                <button
                  onClick={toggleTheme}
                  className="text-foreground-muted hover:text-accent p-2 rounded-lg hover:bg-accent/10 transition-colors"
                  aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
                >
                  {theme === 'dark' ? (
                    <Sun className="w-5 h-5" aria-hidden="true" />
                  ) : (
                    <Moon className="w-5 h-5" aria-hidden="true" />
                  )}
                </button>
                <button
                  onClick={() => {
                    setMobileMenuOpen(false)
                    signOut()
                  }}
                  className="text-foreground-muted hover:text-accent p-2 rounded-lg hover:bg-accent/10 transition-colors"
                  aria-label="Sign out"
                >
                  <LogOut className="w-5 h-5" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>
      <main id="main-content" className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <Outlet />
      </main>
    </div>
  )
}
